import * as React from 'react';
import { cn } from '@/hen-ui/utils/cn';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';

export type StickyColumnProps = {
  heading: string;
  description: string;
  /**
   * Semantic + visual level for the sticky heading. Defaults to `h1` to keep
   * existing consumers unchanged; pass `h2` (etc.) when the page already has an
   * `h1` so the document outline stays valid.
   */
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  className?: string;
  children: React.ReactNode;
};

export const StickyColumn = React.forwardRef<HTMLDivElement, StickyColumnProps>(
  ({ heading, description, headingLevel = 'h1', className, children }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col md:flex-row md:items-start gap-3xl w-full',
          className,
        )}
      >
        {(heading || description) && (
          <div className='w-full md:w-1/2 md:sticky md:self-start top-16 md:top-24'>
            <div className='flex flex-col gap-sm md:py-page-top'>
              {heading && <Heading variant={headingLevel}>{heading}</Heading>}
              {description && <Text variant='b3'>{description}</Text>}
            </div>
          </div>
        )}
        <div className='w-full text-left md:w-1/2'>{children}</div>
      </div>
    );
  },
);

StickyColumn.displayName = 'StickyColumn';
